import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardBody } from '@structural-climbing/ui'
import { useCompetitions } from './useCompetitions'

interface Props {
  gymId: string
  gymSlug: string
  apiUrl: string
  getAccessTokenSilently: () => Promise<string>
}

/**
 * Dashboard call to action. Self-hiding: when the gym has nothing running or
 * coming up, the card does not render at all rather than showing an empty state.
 */
export function CompetitionTeaser({ gymId, gymSlug, apiUrl, getAccessTokenSilently }: Props) {
  const { t } = useTranslation()
  const { competitions, loading } = useCompetitions(gymId, apiUrl, getAccessTokenSilently)

  if (loading) return null

  // The dashboard is about what to do today, so a finished competition has no
  // place on it — its result lives on the Competitions page. The endpoint
  // returns history now, so this has to be filtered here rather than assumed.
  const live = competitions.filter((c) => c.state !== 'ended')
  if (live.length === 0) return null

  // In progress beats upcoming: that is the one a member can act on today.
  const featured =
    live.find((c) => c.state === 'in_progress') ??
    // Otherwise the one starting soonest.
    [...live].sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0]

  return (
    <Link to={`/${gymSlug}/competitions/${featured.id}`} className="block">
      <Card className="shadow-sm transition-transform active:scale-[0.99]">
        <CardBody className="flex-row items-center justify-between gap-3 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t(`competitions.state_${featured.state}`)}
            </p>
            <p className="truncate font-bold">{featured.name}</p>
            <p className="text-sm text-foreground/60">
              {featured.joined ? t('competitions.teaserJoined') : t('competitions.teaserJoin')}
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white">
            {featured.joined ? t('competitions.teaserOpen') : t('competitions.join')}
          </span>
        </CardBody>
      </Card>
    </Link>
  )
}
